/**
 * @file Contributor dashboard UI — pages/Dashboard/Contributor/Stages/A1/FormA1.tsx.
 */

// src/pages/FormA1.tsx
import React from "react";
import "../../../../../styles/formBase.css";
import "./FormA1.css";
import { FormSubSection } from "../../../../../interfaces/formInterfaces";
import { useA1FormData } from "./hooks/useA1FormData";
import A1Section from "./components/A1Section";
import A1EnergySummary from "./components/A1EnergySummary";
import Button from "../../../../../components/common/Button";
import A1CarbonSummary from "./components/A1CarbonSummary";

interface FormA1Props {
  submissionId?: string;
  onNextStage: () => void;
}

const FormA1: React.FC<FormA1Props> = ({ submissionId, onNextStage }) => {
  const {
    formData,
    loading,
    rawMaterials,
    extractionProcesses,
    sectionRows,
    values,
    hasSavedOnce,
    isDirty,
    handleChange,
    energySummaryMj,
    energyPerDeclaredFinishedMaterialMj,
    embodiedCarbonTotalKg,
    embodiedCarbonPerFinishedKg,
    perFinishedEnergyUnitLabel,
    perFinishedCarbonUnitLabel,
    buildPayloadAndSave,
    errors,
    fuelDensityCatalog,
    calorificValuesCatalog,
    backgroundFinishedMaterialUnitSymbol,
  } = useA1FormData({ submissionId });

  if (loading) return <p>Loading Form A1...</p>;
  if (!formData) return <p>No form data found.</p>;

  const sections: FormSubSection[] = formData.formSubSections || [];



  return (
    <div className="form-container formA1-container contributor-stage-form">
      <h2 className="form-heading">{formData.name}</h2>

      {sections.map((section) => {
        const code = section.code || section.name || String(section.id);
        const rowCount = sectionRows[code] || 1;

        return (
          <div key={code} className="form-subsection">
            <h3>{section.name}</h3>

            <A1Section
              section={section}
              rowCount={rowCount}
              rawMaterials={rawMaterials}
              extractionProcesses={extractionProcesses}
              values={values}
              onChange={handleChange}
              errors={errors}
              formData={formData}
              fuelDensityCatalog={fuelDensityCatalog}
              calorificValuesCatalog={calorificValuesCatalog}
              backgroundFinishedMaterialUnitSymbol={
                backgroundFinishedMaterialUnitSymbol
              }
            />

            {code === "A1_EMBODIED_ENERGY" && (
              <A1EnergySummary
                energyMj={energySummaryMj}
                energyPerDeclaredFinishedMj={
                  energyPerDeclaredFinishedMaterialMj
                }
                perFinishedUnitLabel={perFinishedEnergyUnitLabel}
              />
            )}

            {section.code === "A1_EMBODIED_CARBON" && (
              <A1CarbonSummary
                totalKgCo2e={embodiedCarbonTotalKg}
                perFinishedKgCo2e={embodiedCarbonPerFinishedKg}
                perFinishedUnitLabel={perFinishedCarbonUnitLabel}
              />
            )}
          </div>
        );
      })}

      <div className="button-row" style={{ marginTop: 18 }}>
        <Button
          label="Save"
          type="button"
          disabled={loading || !isDirty}
          onClick={async () => {
            await buildPayloadAndSave();
          }}
        />
        <Button
          label="Next"
          type="button"
          disabled={loading || isDirty || !hasSavedOnce}
          onClick={() => {
            onNextStage();
          }}
        />
      </div>
    </div>
  );
};

/** React component — Form A1. */
export default FormA1;